import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Check, Terminal, ChevronRight, ChevronDown, ArrowRight, Shield, Key, RefreshCw, Loader2 } from 'lucide-react';
import Header from '../components/layout/Header';
import { useAuth, API } from '../App';

/* ═══════════════════════════════════════════════════════════════
   Forge Tension palette
   ═══════════════════════════════════════════════════════════════ */
const FT = {
  bg: '#0A0A0A',
  surface: '#1E1E1E',
  surfaceAlt: '#161616',
  border: '#2A2A2A',
  text: '#F5F1E8',
  muted: '#A0A0A0',
  primary: '#FF4D00',
  data: '#00D4FF',
  alert: '#A61E17',
  highlight: '#F2A900',
  success: '#22C55E',
};

const DEFAULT_MCP_URL = 'https://www.getfray.ai/mcp';

/* Local magic prompt — installs MCP + skill in a single paste (#556).
   Uses {{API_KEY}} and {{MCP_URL}} placeholders for substituteVars(). */
const LOCAL_MAGIC_PROMPT = `I need you to set up the Fray MCP server AND the Fray Agent Skill. Please follow these steps in order:

Prerequisites: Desktop Commander must be enabled in your Tools menu before starting. If it's not enabled, enable it now and then paste this prompt again.

Step 1 — Detect OS and locate the config file
Determine whether this is Windows or macOS, then read the Claude Desktop config file:
* Windows: %APPDATA%\\\\Claude\\\\claude_desktop_config.json
* macOS: ~/Library/Application Support/Claude/claude_desktop_config.json
Preserve any existing mcpServers entries in the file.

Step 2 — Add the Fray MCP server entry
Merge the following entry into the mcpServers object and write the file back:

{
  "fray": {
    "command": "npx",
    "args": ["-y", "mcp-remote@latest", "{{MCP_URL}}?api_key={{API_KEY}}"]
  }
}

On Windows, if npx is not on PATH, use the full path:
"command": "C:\\\\\\\\Users\\\\\\\\USERNAME\\\\\\\\AppData\\\\\\\\Roaming\\\\\\\\npm\\\\\\\\npx.cmd"

On macOS, if npx is not on PATH, use: /opt/homebrew/bin/npx

Step 3 — Install the Fray Agent Skill
Create the skills directory and download the Committee Design skill:
* macOS/Linux: mkdir -p ~/.claude/skills/fray && curl -H 'Authorization: Bearer {{API_KEY}}' https://www.getfray.ai/api/skill/committee-design/download -o ~/.claude/skills/fray/SKILL.md
* Windows (PowerShell): New-Item -ItemType Directory -Force -Path "$env:USERPROFILE\\\\.claude\\\\skills\\\\fray"; Invoke-WebRequest -Headers @{'Authorization'='Bearer {{API_KEY}}'} -Uri 'https://www.getfray.ai/api/skill/committee-design/download' -OutFile "$env:USERPROFILE\\\\.claude\\\\skills\\\\fray\\\\SKILL.md"
Confirm the file was created and is non-empty by reading the first few lines.

Step 4 — Tell the user to quit and restart
Print exactly: "Setup complete. Please fully quit Claude Desktop now (Cmd+Q on macOS, Alt+F4 on Windows — not just closing the window) and then relaunch it. After it restarts, paste the verify prompt below into a new conversation."

Step 5 — Display the verify prompt
After the restart instruction, output the following verify prompt for the user to copy:

---
Verify my Fray MCP setup by running these two checks:
1. Run fray_diagnostics — this checks connectivity, database, API keys, and model availability. All sections should show healthy status.
2. Run fray_of_the_day — this runs an actual AI debate on a fun daily topic and confirms the full pipeline is working end-to-end.
Report the results of both. If both complete successfully, Fray MCP is fully operational.
---

Do not attempt to call any Fray tools in this conversation — they will not be available until after the restart.
Server name: fray
API Key: {{API_KEY}}`;

/* ═══════════════════════════════════════════════════════════════
   Platform configurations
   ═══════════════════════════════════════════════════════════════ */
const PLATFORMS = [
  { id: 'claude-code', label: 'Claude Code', icon: '\u2328\uFE0F' },
  { id: 'claude-desktop', label: 'Claude Desktop', icon: '\uD83D\uDDA5\uFE0F' },
  { id: 'cursor', label: 'Cursor', icon: '\uD83D\uDCDD' },
  { id: 'windsurf', label: 'Windsurf', icon: '\uD83C\uDF0A' },
  { id: 'chatgpt', label: 'ChatGPT', icon: '\uD83E\uDD16', unsupported: true },
];

const FEATURE_GROUPS = {
  read_only: { label: 'Read Only', count: 11, desc: 'Browse playbooks, personas, and community content' },
  basic: { label: 'Basic', count: 17, desc: 'Run frays plus browse content' },
  frays_only: { label: 'Frays Only (Legacy)', count: 14, desc: 'Run and manage frays' },
  standard: { label: 'Standard', count: 52, desc: 'Full individual features, sharing, and community' },
  team: { label: 'Team', count: 56, desc: 'Standard plus workspaces and collaboration' },
  all: { label: 'All Features', count: 71, desc: 'Full access including admin and governance tools' },
};

function substituteVars(template, apiKey, mcpUrl, userName) {
  if (!template) return '';
  return template
    .replace(/\{\{API_KEY\}\}/g, apiKey || 'fray_xxxxxxxxxxxx')
    .replace(/\{\{MCP_URL\}\}/g, mcpUrl || DEFAULT_MCP_URL)
    .replace(/\{\{USER_NAME\}\}/g, userName || '');
}

const PLATFORM_CONFIG_KEYS = {
  'claude-code': 'mcp_config_claude_code',
  'claude-desktop': 'mcp_config_claude_desktop',
  'cursor': 'mcp_config_cursor',
  'windsurf': 'mcp_config_windsurf',
};

function getConfigPath(platform, os) {
  if (platform === 'claude-code') return 'Terminal (project scope)';
  if (platform === 'claude-desktop') {
    return os === 'windows'
      ? '%APPDATA%\\Claude\\claude_desktop_config.json'
      : '~/Library/Application Support/Claude/claude_desktop_config.json';
  }
  if (platform === 'cursor') return '.cursor/mcp.json';
  if (platform === 'windsurf') return '~/.codeium/windsurf/mcp_config.json';
  return '';
}

function getDesktopConfig(os, apiKey, mcpUrl) {
  const key = apiKey || 'fray_xxxxxxxxxxxx';
  const url = mcpUrl || DEFAULT_MCP_URL;
  if (os === 'windows') {
    return JSON.stringify({
      mcpServers: {
        fray: {
          command: "C:\\\\Users\\\\USERNAME\\\\AppData\\\\Roaming\\\\npm\\\\mcp-remote.cmd",
          args: [
            url,
            "--transport", "sse-only",
            "--header", `Authorization: Bearer ${key}`
          ]
        }
      }
    }, null, 2);
  }
  return JSON.stringify({
    mcpServers: {
      fray: {
        type: "url",
        url: url,
        headers: { Authorization: `Bearer ${key}` }
      }
    }
  }, null, 2);
}

/* ═══════════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════════ */
export default function McpOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedPlatform, setSelectedPlatform] = useState('claude-code');
  const [selectedOs, setSelectedOs] = useState('macos');
  const [selectedGroup, setSelectedGroup] = useState('standard');
  const [copied, setCopied] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [showKey, setShowKey] = useState(null); // newly generated key full text
  const [templates, setTemplates] = useState({});

  useEffect(() => {
    // Fetch setup templates (public endpoint — no auth needed)
    API.get('/api/mcp/setup-templates')
      .then(res => setTemplates(res.data.templates || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user) loadApiKeys();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadApiKeys = async () => {
    setLoadingKeys(true);
    try {
      const res = await API.get('/api/user/mcp-key');
      const keyList = res.data.keys || [];
      setApiKeys(keyList);
      // Auto-regenerate if the key was auto-generated on invite acceptance (#553)
      // so the magic prompt is immediately usable without manual regeneration
      const active = keyList.find(k => k.is_active);
      if (active && active.name && active.name.includes('auto-generated')) {
        try {
          const regenRes = await API.post('/api/user/mcp-key/regenerate');
          setShowKey(regenRes.data.key);
          const refreshRes = await API.get('/api/user/mcp-key');
          setApiKeys(refreshRes.data.keys || []);
        } catch { /* silent fallback — user can still click regenerate manually */ }
      }
    } catch {
      // User may not have keys yet
      try {
        const res = await API.get('/api/developer/keys');
        setApiKeys(res.data.keys || []);
      } catch { /* ignore */ }
    }
    setLoadingKeys(false);
  };

  const generateKey = async () => {
    setGeneratingKey(true);
    try {
      const res = await API.post('/api/user/mcp-key/regenerate');
      setShowKey(res.data.key);
      await loadApiKeys();
    } catch {
      try {
        const res = await API.post('/api/developer/keys', { name: 'MCP' });
        setShowKey(res.data.key);
        await loadApiKeys();
      } catch { /* ignore */ }
    }
    setGeneratingKey(false);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const activeKey = apiKeys.find(k => k.is_active);
  const keyDisplay = showKey || (activeKey ? `${activeKey.key_prefix}...` : null);
  const platform = PLATFORMS.find(p => p.id === selectedPlatform);
  const mcpUrl = templates.mcp_server_url || DEFAULT_MCP_URL;
  const currentApiKey = showKey || (activeKey ? activeKey.key_prefix + '...' : undefined);
  // Use DB template if it includes the skill step; otherwise fall back to local (#554)
  const magicPromptTemplate = templates.mcp_prompt_template && templates.mcp_prompt_template.includes('Agent Skills')
    ? templates.mcp_prompt_template
    : LOCAL_MAGIC_PROMPT;
  const configTemplateKey = PLATFORM_CONFIG_KEYS[selectedPlatform];
  const config = configTemplateKey && templates[configTemplateKey]
    ? substituteVars(templates[configTemplateKey], currentApiKey, mcpUrl, user?.name)
    : 'MCP is not supported on this platform yet.';

  return (
    <div className="min-h-screen" style={{ background: FT.bg, color: FT.text }}>
      <Header />

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: `linear-gradient(to bottom, ${FT.primary}08, transparent)` }} />
        <div className="max-w-3xl mx-auto px-4 pt-12 pb-10 text-center relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 text-xs font-mono mb-6"
            style={{ background: `${FT.primary}15`, border: `1px solid ${FT.primary}40`, color: FT.primary }}>
            <Terminal className="w-3 h-3" /> MCP INTEGRATION
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase', letterSpacing: '0.02em' }}>
            Connect Fray to Your AI Tools
          </h1>
          <p className="text-sm md:text-base max-w-xl mx-auto" style={{ color: FT.muted }}>
            Add structured multi-perspective AI debates to Claude, Cursor, or Windsurf. One URL, one API key.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pb-20 space-y-6">

        {/* ═══ SKIP THE STEPS — Copy Prompt ═══ */}
        <div className="p-5 border" style={{ background: `${FT.primary}08`, borderColor: `${FT.primary}30` }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">&#9889;</span>
            <h2 className="text-sm font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
              Skip the Steps — Prompt Your Agent
            </h2>
          </div>
          <p className="text-xs mb-3" style={{ color: FT.muted }}>
            Copy this prompt and paste it into Claude Code or Claude Desktop. It will configure everything automatically.
          </p>
          <div className="relative">
            <pre className="text-xs font-mono p-4 whitespace-pre-wrap overflow-x-auto" style={{ background: FT.bg, border: `1px solid ${FT.border}`, color: '#E0E0E0' }}>
              {substituteVars(magicPromptTemplate, currentApiKey, mcpUrl, user?.name)}
            </pre>
            <button
              onClick={() => copyToClipboard(substituteVars(magicPromptTemplate, currentApiKey, mcpUrl, user?.name), 'magic-prompt')}
              className="absolute top-2 right-2 px-3 py-2 flex items-center gap-1.5 text-xs font-medium"
              style={{ background: FT.primary, color: '#000' }}>
              {copied === 'magic-prompt' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy Prompt</>}
            </button>
          </div>
          {!user && (
            <p className="text-[10px] mt-2" style={{ color: FT.highlight }}>
              Sign in to get your personalized prompt with your API key included.
            </p>
          )}
        </div>

        {/* ═══ Section 1: Your API Key ═══ */}
        <div className="p-5 border" style={{ background: FT.surface, borderColor: FT.border }}>
          <div className="flex items-center gap-2 mb-3">
            <Key className="w-4 h-4" style={{ color: FT.data }} />
            <h2 className="text-sm font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
              Your API Key
            </h2>
          </div>

          {!user ? (
            <div>
              <p className="text-xs mb-3" style={{ color: FT.muted }}>Sign in to view or generate your MCP API key.</p>
              <button onClick={() => navigate('/login')} className="px-4 py-2 text-xs font-medium" style={{ background: FT.primary, color: '#000' }}>
                Sign In
              </button>
            </div>
          ) : loadingKeys ? (
            <div className="flex items-center gap-2 text-xs" style={{ color: FT.muted }}>
              <Loader2 className="w-3 h-3 animate-spin" /> Loading keys...
            </div>
          ) : (
            <div>
              {/* URL display */}
              <div className="mb-3">
                <label className="text-[10px] font-mono mb-1 block" style={{ color: FT.muted }}>SERVER URL</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 text-xs font-mono" style={{ background: FT.bg, border: `1px solid ${FT.border}`, color: FT.data }}>
                    {mcpUrl}
                  </code>
                  <button onClick={() => copyToClipboard(mcpUrl, 'url')}
                    className="px-2 py-2 text-xs" style={{ background: FT.border, color: FT.text }}>
                    {copied === 'url' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  </button>
                </div>
              </div>

              {/* Key display */}
              <div className="mb-3">
                <label className="text-[10px] font-mono mb-1 block" style={{ color: FT.muted }}>BEARER TOKEN</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 text-xs font-mono" style={{ background: FT.bg, border: `1px solid ${FT.border}`, color: keyDisplay ? FT.text : FT.muted }}>
                    {keyDisplay || 'No active API key'}
                  </code>
                  {keyDisplay && (
                    <button onClick={() => copyToClipboard(showKey || activeKey?.key_prefix || '', 'key')}
                      className="px-2 py-2 text-xs" style={{ background: FT.border, color: FT.text }}>
                      {copied === 'key' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    </button>
                  )}
                </div>
                {showKey && (
                  <p className="text-[10px] mt-1" style={{ color: FT.highlight }}>
                    Save this key now -- it cannot be retrieved again.
                  </p>
                )}
              </div>

              {/* Generate / Regenerate */}
              <button onClick={generateKey} disabled={generatingKey}
                className="px-4 py-2 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: activeKey ? FT.border : FT.primary, color: activeKey ? FT.text : '#000' }}>
                {generatingKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                {activeKey ? 'Regenerate Key' : 'Generate API Key'}
              </button>
            </div>
          )}
        </div>

        {/* ═══ Section 2: Choose Platform & Config ═══ */}
        <div className="p-5 border" style={{ background: FT.surface, borderColor: FT.border }}>
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4" style={{ color: FT.data }} />
            <h2 className="text-sm font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
              Connect Your App
            </h2>
          </div>

          {/* Platform tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {PLATFORMS.map(p => (
              <button key={p.id} onClick={() => !p.unsupported && setSelectedPlatform(p.id)}
                className="px-3 py-2 text-xs font-mono border transition-colors"
                style={{
                  background: selectedPlatform === p.id ? `${FT.primary}15` : 'transparent',
                  borderColor: selectedPlatform === p.id ? FT.primary : FT.border,
                  color: p.unsupported ? `${FT.muted}60` : (selectedPlatform === p.id ? FT.primary : FT.text),
                  cursor: p.unsupported ? 'not-allowed' : 'pointer',
                  opacity: p.unsupported ? 0.5 : 1,
                }}>
                <span className="mr-1">{p.icon}</span> {p.label}
                {p.unsupported && <span className="ml-1 text-[9px]">(soon)</span>}
              </button>
            ))}
          </div>

          {/* Platform-specific instructions */}
          {selectedPlatform === 'claude-code' ? (
            <div>
              <p className="text-xs mb-2" style={{ color: FT.muted }}>
                <strong style={{ color: FT.text }}>Step 1:</strong> Run this command in your terminal:
              </p>
              <div className="relative mb-4">
                <pre className="text-xs font-mono p-3" style={{ background: FT.bg, border: `1px solid ${FT.border}`, color: '#E0E0E0' }}>
                  {config}
                </pre>
                <button onClick={() => copyToClipboard(config, 'config')}
                  className="absolute top-2 right-2 px-2 py-1.5 flex items-center gap-1 text-[10px] font-mono"
                  style={{ background: FT.border, color: FT.text }}>
                  {copied === 'config' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                </button>
              </div>
              <p className="text-xs mb-2" style={{ color: FT.muted }}>
                <strong style={{ color: FT.text }}>Step 2:</strong> Authenticate via the MCP panel:
              </p>
              <div className="relative mb-4">
                <pre className="text-xs font-mono p-3" style={{ background: FT.bg, border: `1px solid ${FT.border}`, color: '#E0E0E0' }}>claude /mcp</pre>
                <button onClick={() => copyToClipboard('claude /mcp', 'mcp-cmd')}
                  className="absolute top-2 right-2 px-2 py-1.5 flex items-center gap-1 text-[10px] font-mono"
                  style={{ background: FT.border, color: FT.text }}>
                  {copied === 'mcp-cmd' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                </button>
              </div>
              <p className="text-[10px]" style={{ color: FT.muted }}>
                Select the <code style={{ color: FT.primary }}>fray</code> server, then Authenticate.
                Your API key: <code style={{ color: FT.data }}>{keyDisplay || 'fray_xxx'}</code>
              </p>
            </div>
          ) : selectedPlatform === 'claude-desktop' ? (
            <div>
              {/* macOS / Windows sub-panels */}
              <div className="flex gap-2 mb-4">
                {[{ id: 'macos', label: 'macOS', icon: '\uD83C\uDF4E' }, { id: 'windows', label: 'Windows', icon: '\uD83E\uDE9F' }].map(os => (
                  <button key={os.id} onClick={() => setSelectedOs(os.id)}
                    className="px-4 py-2 min-h-[44px] text-xs font-mono border transition-colors flex items-center gap-1.5"
                    style={{
                      background: selectedOs === os.id ? `${FT.primary}15` : 'transparent',
                      borderColor: selectedOs === os.id ? FT.primary : FT.border,
                      color: selectedOs === os.id ? FT.primary : FT.text,
                    }}
                    data-testid={`os-${os.id}`}>
                    <span>{os.icon}</span> {os.label}
                  </button>
                ))}
              </div>

              {/* Windows prerequisite */}
              {selectedOs === 'windows' && (
                <div className="p-3 mb-4 border" style={{ background: `${FT.highlight}10`, borderColor: `${FT.highlight}30` }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: FT.highlight }}>Prerequisite:</p>
                  <div className="relative">
                    <pre className="text-xs font-mono p-2" style={{ background: FT.bg, border: `1px solid ${FT.border}`, color: '#E0E0E0' }}>npm install -g mcp-remote</pre>
                    <button onClick={() => copyToClipboard('npm install -g mcp-remote', 'prereq')}
                      className="absolute top-1 right-1 px-2 py-1 text-[10px] font-mono"
                      style={{ background: FT.border, color: FT.text }}>
                      {copied === 'prereq' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    </button>
                  </div>
                </div>
              )}

              {/* Config snippet */}
              {(() => {
                const desktopConfig = getDesktopConfig(selectedOs, currentApiKey, mcpUrl);
                return (
                  <div>
                    <p className="text-xs mb-2" style={{ color: FT.muted }}>
                      Add this to <code className="px-1" style={{ color: FT.primary, background: `${FT.primary}10` }}>{getConfigPath('claude-desktop', selectedOs)}</code>:
                    </p>
                    <div className="relative mb-3">
                      <pre className="text-xs font-mono p-4 whitespace-pre-wrap overflow-x-auto" style={{ background: FT.bg, border: `1px solid ${FT.border}`, color: '#E0E0E0' }}>
                        {desktopConfig}
                      </pre>
                      <button onClick={() => copyToClipboard(desktopConfig, 'desktop-config')}
                        className="absolute top-2 right-2 px-2 py-1.5 flex items-center gap-1 text-xs font-mono"
                        style={{ background: FT.border, color: FT.text }}>
                        {copied === 'desktop-config' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                      </button>
                    </div>
                    {selectedOs === 'windows' && (
                      <p className="text-[10px]" style={{ color: FT.muted }}>
                        Replace <code style={{ color: FT.primary }}>USERNAME</code> with your Windows username.
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : platform?.unsupported ? (
            <div className="p-3 border" style={{ background: `${FT.highlight}10`, borderColor: `${FT.highlight}30` }}>
              <p className="text-xs" style={{ color: FT.highlight }}>
                {platform.label} does not support MCP yet. We will add support when it becomes available.
              </p>
            </div>
          ) : (
            <div>
              <p className="text-xs mb-2" style={{ color: FT.muted }}>
                Add this to <code className="px-1" style={{ color: FT.primary, background: `${FT.primary}10` }}>{getConfigPath(selectedPlatform)}</code>:
              </p>
              <div className="relative mb-3">
                <pre className="text-xs font-mono p-4 whitespace-pre-wrap overflow-x-auto" style={{ background: FT.bg, border: `1px solid ${FT.border}`, color: '#E0E0E0' }}>
                  {config}
                </pre>
                <button onClick={() => copyToClipboard(config, 'config')}
                  className="absolute top-2 right-2 px-2 py-1.5 flex items-center gap-1 text-xs font-mono"
                  style={{ background: FT.border, color: FT.text }}>
                  {copied === 'config' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
              <p className="text-[10px]" style={{ color: FT.muted }}>
                Replace <code style={{ color: FT.primary }}>fray_xxxxxxxxxxxx</code> with your actual API key from above.
              </p>
            </div>
          )}
        </div>

        {/* ═══ Section 3: Install Agent Skills (Optional) ═══ */}
        <div className="p-5 border" style={{ background: FT.surface, borderColor: FT.border }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm">3</span>
            <h2 className="text-sm font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
              Install Agent Skills <span className="text-[10px] font-normal ml-1" style={{ color: FT.muted, textTransform: 'none' }}>(optional)</span>
            </h2>
          </div>
          <p className="text-xs mb-2" style={{ color: FT.muted }}>
            Give your AI agent built-in knowledge of how to run frays:
          </p>
          <div className="relative">
            <pre className="text-xs font-mono p-3" style={{ background: FT.bg, border: `1px solid ${FT.border}`, color: '#E0E0E0' }}>
              npx @getfray/agent-skills install
            </pre>
            <button onClick={() => copyToClipboard('npx @getfray/agent-skills install', 'skills')}
              className="absolute top-2 right-2 px-2 py-1 flex items-center gap-1 text-[10px] font-mono"
              style={{ background: FT.border, color: FT.text }}>
              {copied === 'skills' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>
        </div>

        {/* ═══ Section 4: Verify ═══ */}
        <div className="p-5 border" style={{ background: FT.surface, borderColor: FT.border }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm">4</span>
            <h2 className="text-sm font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
              Verify Connection
            </h2>
          </div>
          <p className="text-xs mb-2" style={{ color: FT.muted }}>
            Ask your AI agent:
          </p>
          <div className="relative mb-3">
            <pre className="text-xs font-mono p-3" style={{ background: FT.bg, border: `1px solid ${FT.border}`, color: '#E0E0E0' }}>
              Run fray_of_the_day to verify my Fray MCP connection works and see a live debate.
            </pre>
            <button onClick={() => copyToClipboard('Run fray_of_the_day to verify my Fray MCP connection works and see a live debate.', 'verify')}
              className="absolute top-2 right-2 px-2 py-1 flex items-center gap-1 text-[10px] font-mono"
              style={{ background: FT.border, color: FT.text }}>
              {copied === 'verify' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>
          <div className="p-3 border" style={{ background: `${FT.success}10`, borderColor: `${FT.success}30` }}>
            <p className="text-xs" style={{ color: FT.success }}>
              <Check className="w-3 h-3 inline mr-1 -mt-0.5" />
              If you see "Connected to Fray" with your plan and tool count, you are all set.
            </p>
          </div>
        </div>

        {/* ═══ Feature Groups ═══ */}
        <div className="p-5 border" style={{ background: FT.surface, borderColor: FT.border }}>
          <h2 className="text-sm font-bold mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
            Feature Groups by Plan
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            {Object.entries(FEATURE_GROUPS).filter(([k]) => k !== 'frays_only').map(([key, fg]) => (
              <button key={key} onClick={() => setSelectedGroup(key)}
                className="p-2 border text-left transition-colors"
                style={{
                  background: selectedGroup === key ? `${FT.primary}12` : 'transparent',
                  borderColor: selectedGroup === key ? FT.primary : FT.border,
                }}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[10px] font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: selectedGroup === key ? FT.primary : FT.text }}>
                    {fg.label}
                  </span>
                  <span className="text-[9px] font-mono px-1 py-0.5" style={{ background: FT.bg, color: FT.data }}>
                    {fg.count} tools
                  </span>
                </div>
                <span className="text-[9px]" style={{ color: FT.muted }}>{fg.desc}</span>
              </button>
            ))}
          </div>
          <div className="p-3" style={{ background: FT.bg, border: `1px solid ${FT.border}` }}>
            <table className="w-full text-[10px]">
              <thead>
                <tr style={{ borderBottom: `1px solid ${FT.border}` }}>
                  <th className="text-left py-1 font-mono" style={{ color: FT.primary }}>Tier</th>
                  <th className="text-left py-1" style={{ color: FT.muted }}>Default Group</th>
                  <th className="text-right py-1 font-mono" style={{ color: FT.muted }}>Tools</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Free', 'read_only', '11'],
                  ['Student', 'read_only', '11'],
                  ['Pro', 'standard', '52'],
                  ['Team', 'team', '56'],
                  ['Enterprise', 'all', '71'],
                ].map(([tier, group, count]) => (
                  <tr key={tier} style={{ borderBottom: `1px solid ${FT.border}` }}>
                    <td className="py-1.5 font-mono" style={{ color: FT.text }}>{tier}</td>
                    <td className="py-1.5" style={{ color: FT.muted }}>{FEATURE_GROUPS[group]?.label}</td>
                    <td className="py-1.5 text-right font-mono" style={{ color: FT.data }}>{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ═══ FAQ ═══ */}
        <div>
          <h2 className="text-sm font-bold mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
            FAQ
          </h2>
          <div className="space-y-1">
            {[
              { q: 'What plans support MCP?', a: 'All plans have some MCP access. Free and Student plans get read-only tools. Pro, Team, and Enterprise get progressively more features.' },
              { q: 'Do I need to install anything?', a: 'No. Fray uses remote MCP over HTTP -- no npm packages, no local servers. Just add the URL and API key to your client.' },
              { q: 'How do I get an API key?', a: 'Sign in above and click "Generate API Key". The key is shown once -- save it immediately. You can regenerate at any time.' },
              { q: 'Can I use my own model keys (BYOK)?', a: 'Yes. Pro and above support Bring Your Own Key. Configure your provider API keys in Settings.' },
              { q: 'What models are used in debates?', a: 'Each playbook specifies its own model configuration. Most use a mix of economy and standard models. Pro users can access premium models.' },
            ].map((faq, i) => (
              <div key={i} className="border" style={{ background: FT.surface, borderColor: FT.border }}>
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-3 text-left min-h-[44px]">
                  <span className="text-xs font-medium" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{faq.q}</span>
                  {expandedFaq === i
                    ? <ChevronDown className="w-4 h-4 shrink-0" style={{ color: FT.primary }} />
                    : <ChevronRight className="w-4 h-4 shrink-0" style={{ color: FT.muted }} />}
                </button>
                {expandedFaq === i && (
                  <div className="px-3 pb-3">
                    <p className="text-xs" style={{ color: FT.muted }}>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ═══ CTA ═══ */}
        {!user && (
          <div className="p-6 border text-center" style={{ background: `linear-gradient(to right, ${FT.primary}10, ${FT.surface})`, borderColor: `${FT.primary}30` }}>
            <h2 className="text-lg font-bold mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
              Ready to connect?
            </h2>
            <p className="text-xs mb-4" style={{ color: FT.muted }}>Sign up, grab your API key, and start running frays.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <button onClick={() => navigate('/signup')} className="px-6 py-2.5 text-sm font-medium" style={{ background: FT.primary, color: '#000' }}>
                Get Started Free <ArrowRight className="w-4 h-4 inline ml-1" />
              </button>
              <button onClick={() => navigate('/playbooks')} className="px-6 py-2.5 text-sm border" style={{ borderColor: FT.border, color: FT.text }}>
                Browse Playbooks
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
